import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllUsers, getSubunitNames } from '../lib/api/endpoints';
import type { UserDto } from '../lib/api/endpoints';

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  unit: string;
  active: boolean;
  // Campos "crudos" (además de las etiquetas de arriba, que alimentan la
  // tabla) para poder editar el usuario contra el contrato real sin volver a
  // pedir GET /users antes de abrir el modal.
  displayName: string | null;
  roleValue: UserDto['role'];
  subunitIds: string[];
  status: UserDto['status'];
}

export const ROLE_LABEL_FROM_DTO: Record<UserDto['role'], string> = {
  ADMINISTRADOR: 'Administrador',
  SUPERVISOR: 'Supervisor',
  OPERADOR: 'Operador',
  EJECUTIVO: 'Ejecutivo',
};

export function adminUserFromDto(dto: UserDto, subunitNames: Map<string, string>): AdminUser {
  const units = (dto.subunitIds ?? []).map(id => subunitNames.get(id)).filter((n): n is string => !!n);
  return {
    id: dto.id,
    name: dto.displayName ?? dto.email,
    email: dto.email,
    role: ROLE_LABEL_FROM_DTO[dto.role],
    // Vacío = rol consolidado (Administrador/Ejecutivo ven todo el tenant sin
    // filtro de sub-unidad, Paquete_Arranque_Frontend.md §"acceso rápido").
    unit: units.length > 0 ? units.join(', ') : 'Todo el tenant',
    active: dto.status === 'ACTIVE',
    displayName: dto.displayName ?? null,
    roleValue: dto.role,
    subunitIds: dto.subunitIds ?? [],
    status: dto.status,
  };
}

// FE-04 (revisión de paneles, 2026-08-31): AdminPage.tsx mostraba un
// `INITIAL_USERS` hardcodeado (emails @demo.com que nunca existieron en
// Identity Platform) en vez de `GET /users`, ya implementado en el
// contrato. `refetch` permite recargar la lista real tras un alta/edición
// (BE-17, completada 2026-08-31, expone POST/PATCH /users).
export function useUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const cancelledRef = useRef(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [items, subunitNames] = await Promise.all([getAllUsers(), getSubunitNames()]);
      if (cancelledRef.current) return;
      setUsers(items.map(dto => adminUserFromDto(dto, subunitNames)));
      setError(null);
    } catch (err) {
      if (!cancelledRef.current) {
        setUsers([]);
        setError(err instanceof Error ? err : new Error('Error desconocido cargando usuarios'));
      }
    } finally {
      if (!cancelledRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;
    load();
    return () => { cancelledRef.current = true; };
  }, [load]);

  return { users, isLoading, error, refetch: load };
}
